import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Shield, MapPin, Percent, Clock, CreditCard, ArrowRight, Star, CheckCircle, Building2, Pill, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { MemberContext } from '../App';
import axios from 'axios';
import { TelehealthSection } from '../components/TelehealthPromo';

const Home = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [popularDrugs, setPopularDrugs] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { rxgoMemberId, setRxgoMemberId } = useContext(MemberContext);

  const colors = {
    primary: '#2055df',
    primaryDark: '#1a44b8',
    primaryLight: '#4d7df7',
    secondary: '#0f172a',
    accent: '#06b6d4',
    success: '#10b981',
    bg: '#f8fafc',
    white: '#ffffff',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray400: '#94a3b8',
    gray600: '#475569',
    gray700: '#334155',
    gray800: '#1e293b',
    gray900: '#0f172a'
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchPopularDrugs();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPopularDrugs = async () => {
    try {
      const response = await axios.get('/api/drugs/popular');
      setPopularDrugs(response.data.drugs || []);
    } catch (error) {
      console.error('Error fetching popular drugs:', error);
    }
  };

  const searchDrugs = async (query) => {
    if (query.length < 2) { setSuggestions([]); return; }
    setIsSearching(true);
    try {
      const response = await axios.get(`/api/drugs/search/${encodeURIComponent(query)}`, {
        headers: { 'rxgo-member-id': rxgoMemberId }
      });
      setSuggestions(response.data.drugs || []);
      if (response.data.memberId) setRxgoMemberId(response.data.memberId);
    } catch (error) {
      console.error('Search error:', error);
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        searchDrugs(searchQuery);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/drug/${encodeURIComponent(searchQuery.trim().toLowerCase())}`);
    }
  };

  const handleSuggestionClick = (drug) => {
    setShowSuggestions(false);
    navigate(`/drug/${encodeURIComponent((drug.uri || drug.name || drug).toLowerCase())}`);
  };

  const features = [
    { icon: <Percent size={isMobile ? 22 : 24} />, title: 'Save Up to 80%', desc: 'Huge discounts on prescriptions vs retail prices' },
    { icon: <Building2 size={isMobile ? 22 : 24} />, title: '67,000+ Pharmacies', desc: 'CVS, Walgreens, Walmart & more nationwide' },
    { icon: <Shield size={isMobile ? 22 : 24} />, title: '100% Free Forever', desc: 'No fees, no registration, no hidden costs' },
    { icon: <Clock size={isMobile ? 22 : 24} />, title: 'Instant Access', desc: 'Get your card immediately, use it today' }
  ];

  const pharmacies = ['CVS', 'Walgreens', 'Walmart', 'Rite Aid', 'Kroger', 'Costco', 'Target', 'Publix'];

  const stats = [
    { value: '80%', label: 'Average Savings' },
    { value: '67K+', label: 'Pharmacies' },
    { value: '1M+', label: 'Users Helped' },
    { value: '$0', label: 'Cost to You' }
  ];

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Hero Section - NO TOP MARGIN/GAP */}
      <section style={{
        background: colors.primary,
        minHeight: isMobile ? 'auto' : '85vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        padding: isMobile ? '40px 16px 56px' : '60px 24px',
        marginTop: 0
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.05) 0%, transparent 50%)`,
          pointerEvents: 'none'
        }} />
        
        <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%', position: 'relative', zIndex: 1 }}>
          <div style={{
            display: isMobile ? 'block' : 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? 0 : 48,
            alignItems: 'center'
          }}>
            {/* Left Column - Content */}
            <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
              {/* Badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 16px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: 50,
                marginBottom: isMobile ? 20 : 32,
                border: '1px solid rgba(255,255,255,0.15)'
              }}>
                <Star size={14} style={{ color: '#fbbf24' }} fill="#fbbf24" />
                <span style={{ color: 'white', fontSize: isMobile ? 13 : 14, fontWeight: 500 }}>Trusted by over 1 million Americans</span>
              </div>

              {/* Headline */}
              <h1 style={{
                fontSize: isMobile ? 28 : 'clamp(36px, 5vw, 56px)',
                fontWeight: 800,
                color: 'white',
                lineHeight: 1.1,
                marginBottom: isMobile ? 16 : 24,
                letterSpacing: '-0.02em'
              }}>
                Save Up to <span style={{ color: colors.accent }}>80%</span> on<br />
                Your Prescriptions
              </h1>

              <p style={{
                fontSize: isMobile ? 15 : 'clamp(16px, 2vw, 20px)',
                color: 'rgba(255,255,255,0.85)',
                maxWidth: 600,
                margin: isMobile ? '0 auto' : 0,
                marginBottom: isMobile ? 24 : 40,
                lineHeight: 1.6,
                padding: '0 8px'
              }}>
                Compare prices at 67,000+ pharmacies nationwide. Get your free discount card instantly — no signup required.
              </p>

              {/* Search Box */}
              <div ref={searchRef} style={{ maxWidth: 600, margin: isMobile ? '0 auto' : 0, position: 'relative' }}>
                <form onSubmit={handleSearch}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'white',
                    borderRadius: 16,
                    padding: 12,
                    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.4)',
                    gap: 10
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 12px',
                      background: colors.gray100,
                      borderRadius: 10
                    }}>
                      <Search size={20} style={{ color: colors.gray400, flexShrink: 0 }} />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Enter your medication name..."
                        style={{
                          flex: 1,
                          border: 'none',
                          outline: 'none',
                          fontSize: 16,
                          padding: '14px 12px',
                          background: 'transparent',
                          color: colors.gray800,
                          minWidth: 0
                        }}
                      />
                    </div>
                    <button
                      type="submit"
                      style={{
                        width: '100%',
                        padding: '14px 24px',
                        background: colors.primary,
                        color: 'white',
                        border: 'none',
                        borderRadius: 10,
                        fontSize: 16,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8
                      }}
                    >
                      Find Prices <ArrowRight size={18} />
                    </button>
                  </div>
                </form>

                {/* Autocomplete Dropdown */}
                {showSuggestions && suggestions.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    borderRadius: 12,
                    marginTop: 8,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                    overflow: 'hidden',
                    zIndex: 100,
                    maxHeight: 300,
                    overflowY: 'auto'
                  }}>
                    {suggestions.map((drug, i) => (
                      <button
                        key={i}
                        onClick={() => handleSuggestionClick(drug)}
                        style={{
                          width: '100%',
                          padding: '14px 18px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          background: 'transparent',
                          border: 'none',
                          borderBottom: `1px solid ${colors.gray100}`,
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                      >
                        <Pill size={18} style={{ color: colors.primary, flexShrink: 0 }} />
                        <span style={{ color: colors.gray800, fontSize: 15, fontWeight: 500 }}>{drug.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Popular Pills */}
              <div style={{ marginTop: isMobile ? 24 : 32 }}>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginBottom: 12 }}>Popular:</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: isMobile ? 'center' : 'flex-start' }}>
                  {['Lisinopril', 'Metformin', 'Atorvastatin', 'Amlodipine', 'Omeprazole'].map((drug) => (
                    <button
                      key={drug}
                      onClick={() => handleSuggestionClick({ name: drug })}
                      style={{
                        padding: '8px 16px',
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: 50,
                        color: 'white',
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                    >
                      {drug}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column / Mobile Bottom - Animated Card */}
            <motion.div
              initial={{ opacity: 0, x: isMobile ? 0 : 30, y: isMobile ? 20 : 0 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{
                position: 'relative',
                paddingLeft: isMobile ? 0 : 16,
                marginTop: isMobile ? 32 : 0,
                paddingBottom: isMobile ? 24 : 0
              }}
            >
              <motion.div
                whileHover={{ rotate: 0, scale: 1.02 }}
                style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  borderRadius: isMobile ? 16 : 20,
                  padding: isMobile ? '24px 24px' : '32px 36px',
                  boxShadow: '0 25px 60px -12px rgba(0,0,0,0.5)',
                  transform: isMobile ? 'rotate(1deg)' : 'rotate(2deg)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  cursor: 'pointer',
                  maxWidth: isMobile ? 340 : 'none',
                  margin: isMobile ? '0 auto' : 0
                }}
                onClick={() => navigate('/card')}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? 20 : 28 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14 }}>
                    <div style={{
                      width: isMobile ? 40 : 48,
                      height: isMobile ? 40 : 48,
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: isMobile ? 10 : 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: isMobile ? 16 : 20,
                      color: 'white'
                    }}>
                      Rx
                    </div>
                    <div>
                      <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: 'white' }}>Rx Savings Card</div>
                      <div style={{ fontSize: isMobile ? 12 : 14, color: 'rgba(255,255,255,0.7)' }}>ChatWithDr.com</div>
                    </div>
                  </div>
                  <div style={{
                    background: '#fbbf24',
                    color: '#78350f',
                    fontSize: isMobile ? 11 : 12,
                    fontWeight: 700,
                    padding: isMobile ? '4px 10px' : '6px 14px',
                    borderRadius: 50
                  }}>
                    FREE
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 10 : 14, marginBottom: isMobile ? 18 : 24 }}>
                  {[
                    { label: 'BIN:', value: '610378' },
                    { label: 'PCN:', value: 'SC1' },
                    { label: 'GROUP:', value: 'SRXG1222' }
                  ].map((item) => (
                    <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: isMobile ? 13 : 14 }}>{item.label}</span>
                      <span style={{ color: 'white', fontWeight: 700, fontSize: isMobile ? 14 : 16, fontFamily: 'monospace' }}>{item.value}</span>
                    </div>
                  ))}
                </div>

                <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: isMobile ? 12 : 16 }}>
                  <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: isMobile ? 12 : 13, textAlign: 'center' }}>
                    Show this card at any participating pharmacy
                  </p>
                </div>
              </motion.div>

              {/* Floating Badge - Save 80% */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                style={{
                  position: 'absolute',
                  top: isMobile ? -12 : -16,
                  right: isMobile ? 8 : -8,
                  background: colors.success,
                  color: 'white',
                  padding: isMobile ? '8px 14px' : '10px 20px',
                  borderRadius: 50,
                  fontWeight: 700,
                  fontSize: isMobile ? 13 : 16,
                  boxShadow: '0 8px 24px rgba(16,185,129,0.4)'
                }}
              >
                Save 80%
              </motion.div>

              {/* Floating Badge - Trusted */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                style={{
                  position: 'absolute',
                  bottom: isMobile ? 8 : -16,
                  left: isMobile ? 8 : 0,
                  background: 'white',
                  padding: isMobile ? '8px 14px' : '12px 20px',
                  borderRadius: isMobile ? 10 : 14,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                <Star size={isMobile ? 14 : 18} style={{ color: '#fbbf24' }} fill="#fbbf24" />
                <span style={{ fontWeight: 700, color: colors.gray900, fontSize: isMobile ? 12 : 14 }}>Trusted by 1M+ Users</span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Popular Medications Section */}
      <section style={{ background: 'white', padding: isMobile ? '40px 16px' : '80px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 28 : 56 }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px',
              background: `${colors.primary}10`,
              borderRadius: 50,
              marginBottom: 14
            }}>
              <TrendingUp size={16} style={{ color: colors.primary }} />
              <span style={{ color: colors.primary, fontSize: 13, fontWeight: 600 }}>POPULAR MEDICATIONS</span>
            </div>
            <h2 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, color: colors.gray900, marginBottom: 10 }}>
              Find Your Medication
            </h2>
            <p style={{ fontSize: isMobile ? 15 : 18, color: colors.gray600, maxWidth: 600, margin: '0 auto' }}>
              Click on a medication to compare prices at pharmacies near you
            </p>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
            gap: isMobile ? 12 : 16,
            maxWidth: 900,
            margin: '0 auto'
          }}>
            {popularDrugs.slice(0, isMobile ? 6 : 8).map((drug, i) => (
              <button
                key={i}
                onClick={() => handleSuggestionClick({ name: drug.name })}
                style={{
                  background: 'white',
                  border: `2px solid ${colors.gray200}`,
                  borderRadius: 12,
                  padding: isMobile ? 16 : 20,
                  textAlign: 'center',
                  cursor: 'pointer'
                }}
              >
                <div style={{ fontSize: isMobile ? 14 : 15, fontWeight: 600, color: colors.gray800, marginBottom: 4 }}>{drug.name}</div>
                <div style={{ fontSize: isMobile ? 12 : 12, color: colors.gray400, marginBottom: 8 }}>{drug.dosage}</div>
                <div style={{ fontSize: isMobile ? 22 : 24, fontWeight: 700, color: colors.success }}>${drug.price}</div>
                <div style={{ fontSize: 12, color: colors.gray400 }}>from</div>
              </button>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: isMobile ? 28 : 40 }}>
            <button
              onClick={() => navigate('/top-prescriptions')}
              style={{
                padding: isMobile ? '14px 24px' : '14px 32px',
                background: 'transparent',
                border: `2px solid ${colors.primary}`,
                borderRadius: 10,
                color: colors.primary,
                fontSize: isMobile ? 15 : 16,
                fontWeight: 600,
                cursor: 'pointer',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              View All Top 50 Medications
            </button>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section style={{ background: colors.bg, padding: isMobile ? '36px 16px' : '60px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: isMobile ? 20 : 32
          }}>
            {stats.map((stat, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 800, color: colors.primary, marginBottom: 4 }}>{stat.value}</div>
                <div style={{ fontSize: isMobile ? 13 : 14, color: colors.gray600 }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{ background: 'white', padding: isMobile ? '48px 16px' : '80px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 28 : 56 }}>
            <h2 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, color: colors.gray900, marginBottom: 12 }}>
              Why Choose Rx.ChatWithDr.com?
            </h2>
            <p style={{ fontSize: isMobile ? 15 : 18, color: colors.gray600, maxWidth: 600, margin: '0 auto' }}>
              The easiest way to save money on prescription medications
            </p>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))', 
            gap: isMobile ? 16 : 24 
          }}>
            {features.map((f, i) => (
              <div key={i} style={{
                background: colors.bg,
                padding: isMobile ? 24 : 32,
                borderRadius: 16,
                border: `1px solid ${colors.gray200}`
              }}>
                <div style={{
                  width: isMobile ? 48 : 56,
                  height: isMobile ? 48 : 56,
                  background: `${colors.primary}15`,
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.primary,
                  marginBottom: isMobile ? 16 : 20
                }}>
                  {f.icon}
                </div>
                <h3 style={{ fontSize: isMobile ? 17 : 20, fontWeight: 600, color: colors.gray900, marginBottom: 8 }}>{f.title}</h3>
                <p style={{ fontSize: isMobile ? 14 : 15, color: colors.gray600, lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pharmacies */}
      <section style={{ background: colors.bg, padding: isMobile ? '48px 16px' : '80px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, color: colors.gray900, marginBottom: 12 }}>
            Accepted at Major Pharmacies
          </h2>
          <p style={{ fontSize: isMobile ? 15 : 18, color: colors.gray600, marginBottom: isMobile ? 28 : 48 }}>
            Over 67,000 pharmacies nationwide accept our discount card
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 10 : 16, justifyContent: 'center' }}>
            {pharmacies.map((p) => (
              <div key={p} style={{
                padding: isMobile ? '12px 20px' : '16px 32px',
                background: 'white',
                borderRadius: 10,
                border: `1px solid ${colors.gray200}`,
                fontSize: isMobile ? 14 : 16,
                fontWeight: 600,
                color: colors.gray700
              }}>
                {p}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Telehealth Services Promotion */}
      <TelehealthSection />

      {/* CTA Section */}
      <section style={{
        background: colors.primary,
        padding: isMobile ? '56px 16px' : '100px 24px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ fontSize: isMobile ? 26 : 40, fontWeight: 700, color: 'white', marginBottom: 16 }}>
            Get Your Free Discount Card
          </h2>
          <p style={{ fontSize: isMobile ? 15 : 18, color: 'rgba(255,255,255,0.85)', marginBottom: isMobile ? 28 : 40 }}>
            Start saving on your prescriptions today. No signup required.
          </p>
          <button
            onClick={() => navigate('/card')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              padding: isMobile ? '16px 28px' : '18px 40px',
              background: 'white',
              color: colors.primary,
              border: 'none',
              borderRadius: 12,
              fontSize: isMobile ? 16 : 18,
              fontWeight: 600,
              cursor: 'pointer',
              width: '100%',
              maxWidth: 300,
              margin: '0 auto'
            }}
          >
            <CreditCard size={22} />
            Get My Free Card
          </button>
        </div>
      </section>
    </div>
  );
};

export default Home;